import { MetadataDictionary } from '@mkp/metadata/models';
import { Metadata } from '@mkp/shared/models';
import { JobAdCategory, JobAdStopCategory } from '@mkp/tracking/models';
import { Vacancy } from '@mkp/vacancy/models';

export interface JobAdPayload {
  vacancy: Vacancy;
  metadata: MetadataDictionary;
  publicationDays?: number;
}

export const getJobAdCategory = ({
  vacancy,
  metadata,
  publicationDays,
}: JobAdPayload): JobAdCategory => {
  const today = new Date();
  const daysLeft = publicationDays ?? 30;
  return {
    ...getJobAdSharedData(vacancy, metadata),
    job_publication_date: today.toISOString(),
    job_end_date: new Date(today.setDate(today.getDate() + daysLeft)).toISOString(),
    job_days_left: daysLeft,
    job_status: 'n',
  };
};

export const getJobAdStopCategory = ({
  vacancy,
  metadata,
  publicationDays,
}: JobAdPayload): JobAdStopCategory => {
  const today = new Date();
  const creditRedemptions = vacancy._embedded?.creditRedemptions;
  const lastRedeploymentCreatedAt = creditRedemptions[creditRedemptions.length - 1].createdAt;
  const publicationDuration = getDurationInDays(new Date(lastRedeploymentCreatedAt), today);
  return {
    ...getJobAdSharedData(vacancy, metadata),
    job_end_date: today.toISOString(),
    job_days_left:
      publicationDays !== undefined ? publicationDays - publicationDuration : undefined,
    job_status: 'n',
  };
};

const getJobAdSharedData = (vacancy: Vacancy, metadata: MetadataDictionary) => ({
  job_id: vacancy.id,
  job_title: vacancy.jobPosition,
  job_location: vacancy?.address?.city,
  job_employment_grade: `${vacancy.employmentGradeFrom}%-${vacancy.employmentGradeTo}%`,
  job_employment_type: getJobAdEmploymentType(vacancy, metadata),
  job_position: getJobAdPosition(vacancy, metadata),
});

const getJobAdEmploymentType = (
  vacancy: Vacancy,
  metadata: MetadataDictionary
): string | undefined => {
  return vacancy.metadataApiEmploymentTypeId
    ? metadata['contractType'].find(
        (_metadata: Metadata) => _metadata.id === vacancy.metadataApiEmploymentTypeId?.toString()
      )?.text
    : undefined;
};

const getJobAdPosition = (vacancy: Vacancy, metadata: MetadataDictionary): string | undefined => {
  return vacancy.metadataApiEmploymentPositionId
    ? metadata['seniorityLvl'].find(
        (_metadata: Metadata) =>
          _metadata.id === vacancy.metadataApiEmploymentPositionId?.toString()
      )?.text
    : undefined;
};

const getDurationInDays = (startDate: Date, endDate: Date) => {
  // 1. Ensure we are working with timestamps (ms)
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();

  // 2. Calculate difference
  const diffInMs = endMs - startMs;

  // 3. Convert ms to days
  // 1000ms * 60s * 60m * 24h = 86,400,000
  return Math.floor(diffInMs / 86400000);
};
